import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Box } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import WorkshopHeader from './WorkshopHeader';
import WorkshopTable from './WorkshopTable';
import WorkshopModal from './WorkshopModal';
import { toast } from 'react-toastify';

const Ateliers = () => {
  const { user } = useAuth();
  const token = localStorage.getItem('token');
  const [searchTerm, setSearchTerm] = useState('');

  const [workshops, setWorkshops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [selectedWorkshop, setSelectedWorkshop] = useState(null);

  // 🟢 Extraction et normalisation du rôle
  const userRole = Number(user?.role || user?.role_user);

  // 🟢 Gestion dynamique du rolePath selon le rôle (Admin, Praticien, Intervenant)
  const rolePath = useMemo(() => {
    if ([1, 2].includes(userRole)) return 'admin';
    if (userRole === 4) return 'praticien';
    return 'intervenant';
  }, [userRole]);

  // Fonction pour ajouter (vide)
  const handleAdd = () => {
    setSelectedWorkshop(null);
    setOpenModal(true);
  };

  // Fonction pour modifier (rempli)
  const handleEdit = (workshop) => {
    setSelectedWorkshop(workshop);
    setOpenModal(true);
  };

  // Récupération des ateliers avec le rolePath dynamique
  const fetchWorkshops = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/${rolePath}/atelier/g/l/list`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setWorkshops(res.data.result || res.data.data || []);
    } catch (err) {
      console.error('Erreur lors de la récupération des ateliers:', err);
    } finally {
      setLoading(false);
    }
  }, [rolePath, token]);

  useEffect(() => {
    if (user && token) {
      fetchWorkshops();
    }
  }, [fetchWorkshops, user, token]);

  // Filtrage pour la recherche
  const filteredWorkshops = workshops.filter((ws) => {
    const searchLower = searchTerm.toLowerCase();
    const titleMatch = ws.title_ws?.toLowerCase().includes(searchLower);
    const intervenantMatch =
      `${ws.intervenant?.nom_user} ${ws.intervenant?.prenom_user}`
        .toLowerCase()
        .includes(searchLower);

    return titleMatch || intervenantMatch;
  });

  // Suppression d'un atelier avec le rolePath dynamique
  const handleDeleteWorkshop = async (idWs) => {
    try {
      const res = await axios.delete(
        `${process.env.REACT_APP_API_URL}/${rolePath}/atelier/meet/d/delete/${idWs}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data.status === 'success') {
        toast.success('Atelier supprimé avec succès');
        fetchWorkshops();
      } else {
        toast.error(res.data.message || 'Erreur lors de la suppression');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erreur serveur');
    }
  };

  return (
    <Box sx={{ p: 4 }}>
      <WorkshopHeader 
        count={filteredWorkshops.length}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        onAddNew={handleAdd}
      />

      <WorkshopTable 
        data={filteredWorkshops} 
        onDelete={handleDeleteWorkshop}
        onEdit={handleEdit}
        loading={loading} // Optionnel : si votre table gère un loader
      />

      <WorkshopModal 
        open={openModal} 
        handleClose={() => setOpenModal(false)} 
        refreshData={fetchWorkshops}
        initialData={selectedWorkshop}
        // 🟢 ID Intervenant/Praticien auto-assigné si rôle 3 ou 4, sinon sélectionné depuis l'atelier
        intervenantId={[3, 4].includes(userRole) ? user?.id : selectedWorkshop?.intervenant_id}
      />
    </Box>
  );
};

export default Ateliers;
